import { isPlatformBrowser } from '@angular/common';
import { inject, Injectable, PLATFORM_ID } from '@angular/core';
import { Observable, tap } from 'rxjs';

import { APIConstants } from '../constants/API.constansts';
import { GoogleConnectionRequest, UserModel } from '../models/user.model';
import { BaseAPIService } from './base-api.service';

const STORAGE_KEY_USER = 'ccl_user';

@Injectable({
  providedIn: 'root',
})
export class AuthService extends BaseAPIService {
  private readonly platformId = inject(PLATFORM_ID);

  public login(username: string, password: string) {
    return this.post<UserModel>(APIConstants.LOGIN_ENDPOINT, { username, password }).pipe(
      tap((user) => this.saveUser(user)),
    );
  }

  public whoAmI() {
    return this.get<UserModel>(APIConstants.WHOAMI_ENDPOINT).pipe(
      tap((user) => this.saveUser(user)),
    );
  }

  public getUser(): UserModel | null {
    if (!isPlatformBrowser(this.platformId)) {
      return null;
    }

    const raw = localStorage.getItem(STORAGE_KEY_USER);

    if (!raw) {
      return null;
    }

    try {
      return JSON.parse(raw) as UserModel;
    } catch {
      return null;
    }
  }

  public isGoogleAccountConnectedLocal(): boolean {
    if (!isPlatformBrowser(this.platformId)) {
      return false;
    }

    const raw = localStorage.getItem('ccl_google_account_connected');
    if (!raw) {
      return false;
    }

    try {
      return JSON.parse(raw) as boolean;
    } catch {
      return false;
    }
  }

  public isGoogleAccountConnected(userId: string): Observable<boolean> {
    return this.get<boolean>(APIConstants.IS_GOOGLE_ACCOUNT_CONNECTED(userId));
  }

  public saveGoogleAccountConnectionStatus(isConnected: boolean): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    localStorage.setItem('ccl_google_account_connected', JSON.stringify(isConnected));
  }

  public saveUser(user: UserModel): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    this.isGoogleAccountConnected(user.id).subscribe((isConnected) => {
      localStorage.setItem('ccl_google_account_connected', JSON.stringify(isConnected));
    });

    localStorage.setItem(STORAGE_KEY_USER, JSON.stringify(user));
  }

  public static getUser(): UserModel | null {
    // Static methods cannot use the injected PLATFORM_ID.
    // Check for the browser global directly.
    if (typeof localStorage === 'undefined') {
      return null;
    }

    const raw = localStorage.getItem(STORAGE_KEY_USER);

    if (!raw) {
      return null;
    }

    try {
      return JSON.parse(raw) as UserModel;
    } catch {
      return null;
    }
  }

  public logout() {
    BaseAPIService.clearCache();
    localStorage.removeItem(STORAGE_KEY_USER);
    localStorage.removeItem('ccl_google_account_connected');

    return this.get<void>(APIConstants.LOGOUT_ENDPOINT).pipe(
      tap(() => {
        if (isPlatformBrowser(this.platformId)) {
        }
      }),
    );
  }

  public setPassword(resetPasswordForm: unknown): Observable<string | null> {
    return this.post<string | null>(APIConstants.SET_PASSWORD_ENDPOINT, resetPasswordForm);
  }

  public forgotPassword(forgotPasswordForm: unknown): Observable<string | null> {
    return this.post<string | null>(
      APIConstants.REQUEST_PASSWORD_FORGOT_ENDPOINT,
      forgotPasswordForm,
    );
  }
}
